import React, { Component } from "react";
import { DataTableToolbar } from "../../components/ui";
import AppBadge from "../../components/ui/AppBadge";
import { BoricaPaymentListItem } from "../../models/BoricaTransaction";
import { toastService } from "../../services/toastService";
import { boricaTransactionApiService } from "../../services/api/transactions/BoricaTransactionApiService";
import { utils } from "../../utils/utils";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { Column } from "primereact/column";
import { stringFilterMatchModeOptions, numberFilterMatchModeOptions, statusFilterMatchModeOptions } from "../Paginator";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";

const BORICA_STATUS_MAP: Record<string, { label: string; variant: "success" | "danger" | "warning" | "orange" | "neutral" | "default" }> = {
    "Initiated": { label: "Initiated", variant: "neutral" },
    "0": { label: "Initiated", variant: "neutral" },
    "BoricaSuccess": { label: "Borica Success", variant: "success" },
    "1": { label: "Borica Success", variant: "success" },
    "BoricaFailed": { label: "Borica Failed", variant: "danger" },
    "2": { label: "Borica Failed", variant: "danger" },
    "Completed": { label: "Completed", variant: "success" },
    "3": { label: "Completed", variant: "success" },
    "FastPayFailed": { label: "FastPay Failed", variant: "danger" },
    "4": { label: "FastPay Failed", variant: "danger" },
    "SystemError": { label: "System Error", variant: "danger" },
    "5": { label: "System Error", variant: "danger" },
};

interface BoricaPaymentsProps {
    extendedReportingEnabled?: boolean;
}

interface BoricaPaymentsState {
    payments: BoricaPaymentListItem[];
    isLoading: boolean;
    totalRecords: number;
    selectedPayment: BoricaPaymentListItem | null;
    isDetailVisible: boolean;
    fromDate: Date | null;
    toDate: Date | null;
    isExporting: boolean;
}

export default class BoricaPayments extends Component<BoricaPaymentsProps, BoricaPaymentsState> {
    private dataTable: ExtendedDataTable | null = null;
    private lastSearchRequest: SearchRequestBaseModel | null = null;

    constructor(props: BoricaPaymentsProps) {
        super(props);
        this.state = {
            payments: [],
            isLoading: false,
            totalRecords: 0,
            selectedPayment: null,
            isDetailVisible: false,
            fromDate: null,
            toDate: null,
            isExporting: false,
        };
    }

    loadData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });
        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);

        // Inject date range filters
        if (this.state.fromDate) {
            searchRequest.filters = [
                ...searchRequest.filters.filter(f => f.columnName !== "fromDate"),
                { columnName: "fromDate", operator: "equals", value: this.state.fromDate.toISOString() }
            ];
        }
        if (this.state.toDate) {
            searchRequest.filters = [
                ...searchRequest.filters.filter(f => f.columnName !== "toDate"),
                { columnName: "toDate", operator: "equals", value: this.state.toDate.toISOString() }
            ];
        }

        this.lastSearchRequest = { ...searchRequest };

        const result = await boricaTransactionApiService.searchBoricaPayments(searchRequest);
        if (!result.success) {
            toastService.error(result.message);
        }
        this.setState({
            payments: result.data ?? [],
            totalRecords: result.totalCount,
            isLoading: false,
        });
    };

    onDateApply = () => {
        if (this.dataTable) {
            this.dataTable.reset();
        }
    };

    onDateClear = () => {
        this.setState({ fromDate: null, toDate: null }, () => {
            if (this.dataTable) {
                this.dataTable.reset();
            }
        });
    };

    exportCsv = async () => {
        if (!this.lastSearchRequest) return;

        this.setState({ isExporting: true });
        try {
            const exportRequest: SearchRequestBaseModel = {
                ...this.lastSearchRequest,
                pageNumber: 1,
                pageSize: 50000,
            };

            const result = await boricaTransactionApiService.exportBoricaPaymentsCsv(exportRequest);
            if (!result.success) {
                toastService.error(result.message || "Failed to export CSV");
                return;
            }

            utils.downloadCSVFromBase64(result.data, "borica_payments_export.csv");
        } catch {
            toastService.error("Failed to export CSV");
        } finally {
            this.setState({ isExporting: false });
        }
    };

    renderHeader = (hasActiveFilters: boolean) => {
        const hasDateFilter = this.state.fromDate !== null || this.state.toDate !== null;
        const { extendedReportingEnabled } = this.props;

        return (
            <DataTableToolbar
                onClear={() => { this.setState({ fromDate: null, toDate: null }, () => this.dataTable!.reset()); }}
                hasActiveFilters={hasActiveFilters || hasDateFilter}
                actions={
                    extendedReportingEnabled ? (
                        <div className="flex items-center gap-2 flex-wrap">
                            <Calendar
                                value={this.state.fromDate}
                                onChange={(e) => this.setState({ fromDate: e.value as Date | null })}
                                dateFormat="dd.mm.yy"
                                showIcon
                                placeholder="From"
                                maxDate={this.state.toDate || new Date()}
                                inputClassName="p-inputtext-sm" inputStyle={{ marginRight: "4px" }}
                                style={{ width: "185px" }}
                            />
                            <Calendar
                                value={this.state.toDate}
                                onChange={(e) => this.setState({ toDate: e.value as Date | null })}
                                dateFormat="dd.mm.yy"
                                showIcon
                                placeholder="To"
                                minDate={this.state.fromDate || undefined}
                                maxDate={new Date()}
                                inputClassName="p-inputtext-sm" inputStyle={{ marginRight: "4px" }}
                                style={{ width: "185px" }}
                            />
                            <Button
                                label="Apply"
                                icon="pi pi-check"
                                className="p-button-sm"
                                disabled={!this.state.fromDate || !this.state.toDate}
                                onClick={this.onDateApply}
                            />
                            <span className="border-l border-neutral-300 h-6 mx-1" />
                            <Button
                                icon="pi pi-download"
                                className="p-button-sm p-button-outlined"
                                onClick={this.exportCsv}
                                loading={this.state.isExporting}
                                tooltip="Export CSV"
                                tooltipOptions={{ position: "top" }}
                                label="Export CSV"
                            />
                        </div>
                    ) : undefined
                }
            />
        );
    };

    statusBodyTemplate = (rowData: BoricaPaymentListItem) => {
        const info = BORICA_STATUS_MAP[rowData.status] ?? { label: rowData.status, variant: "default" as const };
        return <AppBadge variant={info.variant}>{info.label}</AppBadge>;
    };

    dateBodyTemplate = (rowData: BoricaPaymentListItem) => {
        if (!rowData.createdAt) return "-";
        const d = new Date(rowData.createdAt);
        if (isNaN(d.getTime())) return "-";
        return utils.formatDateTimeSofia(d);
    };

    onRowClick = (event: any) => {
        this.setState({ selectedPayment: event.data as BoricaPaymentListItem, isDetailVisible: true });
    };

    render() {
        const { selectedPayment, isDetailVisible } = this.state;
        const { extendedReportingEnabled } = this.props;

        return (
            <>
                <ExtendedDataTable
                    header={this.renderHeader}
                    ref={(ref) => (this.dataTable = ref)}
                    value={this.state.payments}
                    onChange={this.loadData}
                    loading={this.state.isLoading}
                    totalRecords={this.state.totalRecords}
                    defaultSortField="transactionId"
                    defaultSortOrder={SortOrderType.Descending}
                    onRowClick={this.onRowClick}
                >
                    <Column {...({ alwaysVisible: true } as any)} field="transactionId" header="ID" filterMatchModeOptions={numberFilterMatchModeOptions} />
                    <Column {...({ alwaysVisible: true, sortable: false } as any)} field="firstName" header="First Name" filterMatchModeOptions={stringFilterMatchModeOptions} />
                    <Column {...({ alwaysVisible: true, sortable: false } as any)} field="lastName" header="Last Name" filterMatchModeOptions={stringFilterMatchModeOptions} />
                    <Column field="email" header="Email" filterMatchModeOptions={stringFilterMatchModeOptions} sortable={false} />
                    <Column field="orderId" header="Order ID" filterMatchModeOptions={stringFilterMatchModeOptions} />
                    <Column field="amount" header="Amount" filterMatchModeOptions={numberFilterMatchModeOptions} />
                    <Column field="payDutyRequestsCount" header="Duties" />
                    {extendedReportingEnabled && (
                        <Column field="createdAt" header="Transaction Date" body={this.dateBodyTemplate} sortable />
                    )}
                    <Column
                        field="status"
                        header="Status"
                        body={this.statusBodyTemplate}
                        filterMatchModeOptions={statusFilterMatchModeOptions}
                    />
                </ExtendedDataTable>

                {selectedPayment && (
                    <Dialog
                        header="Borica Payment Details"
                        visible={isDetailVisible}
                        style={{ width: "550px" }}
                        contentStyle={{ maxHeight: "70vh", overflowY: "auto", position: "relative" }}
                        modal
                        onHide={() => this.setState({ isDetailVisible: false, selectedPayment: null })}
                        footer={
                            <Button label="Close" icon="pi pi-times"
                                onClick={() => this.setState({ isDetailVisible: false, selectedPayment: null })}
                                className="p-button-text" />
                        }
                    >
                        <div className="flex flex-col gap-3">
                            <SectionHeader title="User" />
                            <DetailRow label="First Name" value={selectedPayment.firstName} />
                            <DetailRow label="Last Name" value={selectedPayment.lastName} />
                            <DetailRow label="Email" value={selectedPayment.email} />
                            <DetailRow label="Phone Number" value={selectedPayment.phoneNumber} />

                            <SectionHeader title="Payment" />
                            <DetailRow label="Transaction ID" value={selectedPayment.transactionId} />
                            <DetailRow label="Order ID" value={selectedPayment.orderId} />
                            <DetailRow label="Amount" value={`${selectedPayment.amount} ${selectedPayment.currency}`} />
                            <DetailRow label="Description" value={selectedPayment.description} />
                            <DetailRow label="Approval Code" value={selectedPayment.approvalCode} />
                            <DetailRow label="Response Code" value={selectedPayment.callbackRc} />
                            <DetailRow label="RRN" value={selectedPayment.callbackRrn} />

                            <SectionHeader title="FastPay Duties" />
                            <DetailRow label="Duty Requests Count" value={selectedPayment.payDutyRequestsCount} />
                            <DetailRow label="Successful Amount" value={selectedPayment.fastPaySuccessAmount} />

                            <SectionHeader title="Status & Dates" />
                            <DetailRow label="Status" value={selectedPayment.status} />
                            <DetailRow label="Created" value={fmtDate(selectedPayment.createdAt)} />
                            <DetailRow label="Updated" value={fmtDate(selectedPayment.updatedAt)} />

                            {selectedPayment.errorMessage && (
                                <>
                                    <SectionHeader title="Error" />
                                    <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{selectedPayment.errorMessage}</div>
                                </>
                            )}
                        </div>
                    </Dialog>
                )}
            </>
        );
    }
}

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
    <div className="font-semibold text-sm text-neutral-500 border-b border-neutral-200 pb-1 mt-2">{title}</div>
);

const str = (val: unknown): string => (val === null || val === undefined) ? "" : String(val);

const fmtDate = (val: unknown): string => {
    if (!val) return "-";
    return utils.formatDateTimeSofia(val as string);
};

const DetailRow: React.FC<{ label: string; value: unknown }> = ({ label, value }) => (
    <div className="flex items-center gap-3">
        <span className="font-semibold text-sm w-44 shrink-0">{label}</span>
        <span className="text-sm">{str(value) || "-"}</span>
    </div>
);
